import { useState, useEffect } from 'react'
import { useProfile, useProviderProfile, useUpdateProfile, useUpdateProviderProfile } from '@/hooks/useProfile'
import Input from '@/components/forms/Input/Input'
import PhoneInput from '@/components/forms/PhoneInput/PhoneInput'
import Button from '@/components/common/Button/Button'
import './Steps.css'

interface PersonalInfoStepProps {
    onComplete?: () => void
}

const PersonalInfoStep = ({ onComplete }: PersonalInfoStepProps) => {
    const { data: profileData, isLoading: profileLoading } = useProfile()
    const { data: providerData, isLoading: providerLoading } = useProviderProfile()
    const updateProfile = useUpdateProfile()
    const updateProviderProfile = useUpdateProviderProfile()

    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [nationalId, setNationalId] = useState('')
    const [phoneNumber, setPhoneNumber] = useState('')
    const [bio, setBio] = useState('')
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (profileData && providerData) {
            setFirstName(profileData.first_name || '')
            setLastName(profileData.last_name || '')
            setNationalId(profileData.national_id_number || '')
            setPhoneNumber(profileData.phone_number || '')
            setBio(providerData.bio || '')
        }
    }, [profileData, providerData])

    const handleSave = async () => {
        // Validation
        if (!firstName.trim()) {
            alert('El nombre es requerido')
            return
        }
        if (!lastName.trim()) {
            alert('El apellido es requerido')
            return
        }
        if (!nationalId.trim()) {
            alert('El DNI es requerido')
            return
        }

        setSaving(true)
        try {
            // Update base profile
            await updateProfile.mutateAsync({
                first_name: firstName,
                last_name: lastName,
                national_id_number: nationalId,
                phone_number: phoneNumber,
            })

            // Update provider profile with bio
            await updateProviderProfile.mutateAsync({
                bio: bio,
            })

            alert('Información actualizada correctamente')
            onComplete?.()
        } catch (error) {
            console.error('Error updating profile:', error)
            alert('Error al actualizar la información')
        } finally {
            setSaving(false)
        }
    }

    if (profileLoading || providerLoading) {
        return <div className="step-loading">Cargando información...</div>
    }

    return (
        <div className="step-container">
            <div className="step-section">
                <h4 className="step-section-title">Información Personal</h4>
                
                <div className="step-input-row">
                    <div className="step-input-half">
                        <Input
                            name="firstName"
                            label="Nombre"
                            placeholder="Tu nombre"
                            value={firstName}
                            setValue={setFirstName}
                            isRequired
                        />
                    </div>
                    <div className="step-input-half">
                        <Input
                            name="lastName"
                            label="Apellido"
                            placeholder="Tu apellido"
                            value={lastName}
                            setValue={setLastName}
                            isRequired
                        />
                    </div>
                </div>

                <Input
                    name="nationalId"
                    label="DNI / Cédula"
                    placeholder="12345678"
                    value={nationalId}
                    setValue={setNationalId}
                    type="text"
                    isRequired
                />

                <PhoneInput
                    label="Teléfono"
                    value={phoneNumber}
                    setValue={setPhoneNumber}
                />
            </div>

            <div className="step-section">
                <h4 className="step-section-title">Describe tu experiencia y habilidades</h4>
                <Input
                    name="bio"
                    label="Descripción del perfil"
                    placeholder="Describe tu experiencia, habilidades y lo que te hace especial..."
                    value={bio}
                    setValue={setBio}
                    multiline
                    rows={4}
                />
                <p className="step-hint">Una buena descripción ayudará a los clientes a entender en que te especializas y que te hace diferente.</p>
            </div>

            {/* <div className="step-tips">
                <h5 className="step-tips-title">💡 Consejos</h5>
                <div className="step-tip">• Usa tu nombre real para generar confianza</div>
                <div className="step-tip">• Asegúrate de que tu teléfono esté actualizado</div>
                <div className="step-tip">• Una buena biografía te ayuda a destacar</div>
            </div> */}

            <div className="step-actions">
                <Button
                    variant="primary"
                    size="lg"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
            </div>
        </div>
    )
}

export default PersonalInfoStep
